import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  BarElement,
  BarController,
  Tooltip,
  Legend,
} from 'chart.js';
import { currency } from '../../utils';
import { Kfh_AreaType, Kfh_RegionType } from '../../types/graphqlTypes';
import { SEO } from '../Seo';
import { PageBackButton } from '../PageBackButton';
import { Section } from '../Section';
import { Typography } from '../Typography';
import { ExpanderPanelGroup } from '../ExpanderPanelGroup';
import { ExpanderPanel } from '../ExpanderPanel';
import { AverageChange } from './AverageChange';
import { LetterRoundel } from '../LetterRoundel';
import { colors } from '../../styles/tokens';
import { LocationNavigation } from './LocationNavigation';
import { LocationActionSection } from './LocationActionSection';
import { LocationActionSectionTest } from './LocationActionSectionTest';
import { LocationActionSectionLeftTest } from './LocationActionSectionLeftTest';
import { SideAndMain, StyledSide } from '../layout/SideAndMain';
import { StyledTableCell, StyledTableHeaderCell, StyledTableRow, Table } from '../Table';
import { SoldDataDirectionValue } from './DirectionValue';
import { Spacer } from '../Spacer';
import { useVariation } from '../../hooks/useVariation';

ChartJS.register(
  BarController,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  CategoryScale,
  BarElement,
  Tooltip,
  Legend
);

interface LocationCouncilTaxProps {
  location: Kfh_AreaType | Kfh_RegionType;
}

const StyledSubHeading = styled(Typography)`
  ${({ theme: { space } }) => css`
    margin-bottom: ${space.lg};
  `};
`;

const StyledChartWrapper = styled.div`
  height: 400px;
  width: 100%;
`;

const StyledSpacer = styled(Spacer)`
  ${({ theme: { space, media } }) => css`
    position: relative;
    top: 0;
    width: calc(100% - ${space.md});

    @media (min-width: ${media.sm}) {
      width: calc(100% - 48px);
    }

    @media (min-width: ${media.md}) {
      width: 100%;
    }
  `};
`;

const StyledLocationNavigation = styled(LocationNavigation)`
  position: relative;
  top: 0;
`;

const LocationCouncilTax: FC<LocationCouncilTaxProps> = ({ location }) => {
  const { name, relativeUrl, seoPages } = location;
  const boroughs = location?.relatedBoroughs?.edges;
  const region = 'region' in location ? location?.region : undefined;

  // Categorisation

  const isArea = !!region;
  const defaultMultisearch = location.artirixMultiSearch
    ? {
        name: location.artirixDisplayName,
        multiSearch: location.artirixMultiSearch,
      }
    : {};

  const { isTestVariationA } = useVariation('KFH25003');

  return (
    <>
      {/* Back button */}

      <PageBackButton url={relativeUrl} label={`${name} overview`} />

      <Section contained>
        <SideAndMain isTestVariation={isTestVariationA}>
          {/* Main content */}
          <Spacer responsive>
            <Typography component="h1" variant="displaySmall">
              {name} Council Tax
            </Typography>
            <ExpanderPanelGroup>
              <StyledSubHeading component="h2" variant="titleLarge">
                {name} covers multiple London boroughs
              </StyledSubHeading>
              <ul>
                {boroughs &&
                  boroughs.map((borough, index) => {
                    const { name, councilTaxModel } = borough?.node || {};
                    const chartDataset = councilTaxModel?.councilTaxBands;

                    // Chart labels
                    const averageBandLabels = chartDataset?.filter((stat) => stat.name === 'A');
                    const chartLabels = averageBandLabels?.map((stat) => stat.year);

                    // Chart data

                    const getBandData = (band) => {
                      const averageBandPrices = chartDataset?.filter((stat) => stat.name === band);
                      return averageBandPrices?.map((stat) => stat.tax);
                    };

                    const averageBandAPricesData = getBandData('A');
                    const averageBandBPricesData = getBandData('B');
                    const averageBandCPricesData = getBandData('C');
                    const averageBandDPricesData = getBandData('D');
                    const averageBandEPricesData = getBandData('E');
                    const averageBandFPricesData = getBandData('F');
                    const averageBandGPricesData = getBandData('G');
                    const averageBandHPricesData = getBandData('H');

                    // Average council tax over time

                    const datasetOptions = {
                      fill: false,
                      lineTension: 0.3,
                      spanGaps: true,
                    };

                    const chartData = {
                      labels: chartLabels,
                      datasets: [
                        {
                          label: 'Band A',
                          data: averageBandAPricesData,
                          backgroundColor: colors.brand.default,
                          borderColor: colors.brand.default,
                          ...datasetOptions,
                        },
                        {
                          label: 'Band B',
                          data: averageBandBPricesData,
                          backgroundColor: colors.accent2.default,
                          borderColor: colors.accent2.default,
                          ...datasetOptions,
                        },
                        {
                          label: 'Band C',
                          data: averageBandCPricesData,
                          backgroundColor: colors.accent1.default,
                          borderColor: colors.accent1.default,
                          ...datasetOptions,
                        },
                        {
                          label: 'Band D',
                          data: averageBandDPricesData,
                          backgroundColor: colors.accent3.default,
                          borderColor: colors.accent3.default,
                          ...datasetOptions,
                        },
                        {
                          label: 'Band E',
                          data: averageBandEPricesData,
                          backgroundColor: colors.accent4.default,
                          borderColor: colors.accent4.default,
                          ...datasetOptions,
                        },
                        {
                          label: 'Band F',
                          data: averageBandFPricesData,
                          backgroundColor: colors.brand.default,
                          borderColor: colors.brand.default,
                          ...datasetOptions,
                        },
                        {
                          label: 'Band G',
                          data: averageBandGPricesData,
                          backgroundColor: colors.accent2.default,
                          borderColor: colors.accent2.default,
                          ...datasetOptions,
                        },
                        {
                          label: 'Band H',
                          data: averageBandHPricesData,
                          backgroundColor: colors.accent1.default,
                          borderColor: colors.accent1.default,
                          ...datasetOptions,
                        },
                      ],
                    };

                    const chartOptions = {
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            callback(value) {
                              return currency(value);
                            },
                          },
                        },
                      },
                      plugins: {
                        legend: {
                          position: 'bottom',
                          labels: {
                            usePointStyle: true,
                            boxWidth: 18,
                            color: colors.black.default,
                            font: {
                              weight: 'bold',
                              size: 14,
                              lineHeight: 22,
                            },
                          },
                        },
                        tooltip: {
                          callbacks: {
                            label(context) {
                              return currency(context.parsed.y);
                            },
                          },
                        },
                      },
                    };

                    return (
                      <ExpanderPanel
                        key={`borough-${index}-${borough?.node?.id}`}
                        as="li"
                        title={`${name} ${councilTaxModel?.currentTaxYear}`}
                        open={index === 0}
                      >
                        <Spacer>
                          <Spacer spacing="quarter">
                            <Typography component="p" variant="lead">
                              Annual rate change:
                            </Typography>
                            <AverageChange
                              average={`${councilTaxModel?.rateChange}%`}
                              direction={councilTaxModel?.direction}
                              useDirectionIcon
                            />
                            <Typography component="p" variant="bodySmall">
                              Source - Council tax data from {name} County Council
                            </Typography>
                          </Spacer>
                          <Table shadowWrapper>
                            <thead>
                              <StyledTableRow sticky>
                                <StyledTableHeaderCell>Band*</StyledTableHeaderCell>
                                <StyledTableHeaderCell>{councilTaxModel?.previousTaxYear}</StyledTableHeaderCell>
                                <StyledTableHeaderCell>{councilTaxModel?.currentTaxYear}</StyledTableHeaderCell>
                                <StyledTableHeaderCell>Annual rate change</StyledTableHeaderCell>
                              </StyledTableRow>
                            </thead>
                            <tbody>
                              {councilTaxModel?.taxBands &&
                                councilTaxModel?.taxBands.map((taxBand, modelIndex) => (
                                  <tr key={`councilTaxModel-${modelIndex}`}>
                                    <StyledTableCell>
                                      <LetterRoundel letter={taxBand.band} />
                                    </StyledTableCell>
                                    <StyledTableCell>{currency(taxBand.lastYear)}</StyledTableCell>
                                    <StyledTableCell>{currency(taxBand.thisYear)}</StyledTableCell>
                                    <StyledTableCell>
                                      <SoldDataDirectionValue
                                        value={`${taxBand.rateChange}%`}
                                        direction={taxBand.direction}
                                      />
                                    </StyledTableCell>
                                  </tr>
                                ))}
                            </tbody>
                          </Table>
                          <Typography component="p" variant="smallprint">
                            *Every property has been allocated one of eight bands according to it's capital value as at
                            1 April 1991
                          </Typography>
                          <StyledChartWrapper>
                            <Line data={chartData} options={chartOptions} />
                          </StyledChartWrapper>
                        </Spacer>
                      </ExpanderPanel>
                    );
                  })}
              </ul>
            </ExpanderPanelGroup>
          </Spacer>

          {/* Side navigation */}
          <StyledSide>
            <StyledSpacer>
              <StyledLocationNavigation url={relativeUrl} name={name} isArea={isArea} />

              {isTestVariationA && <LocationActionSectionLeftTest />}
            </StyledSpacer>
          </StyledSide>
        </SideAndMain>
      </Section>

      {/* Actions */}

      {isTestVariationA ? (
        <LocationActionSectionTest />
      ) : (
        <LocationActionSection defaultMultisearch={defaultMultisearch} />
      )}
    </>
  );
};

export default LocationCouncilTax;
